import React, { useState } from "react";

function RegisterUser({ onClose, clinicId }) {
    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        username: "",
        password: "",
        role: "",
        specialization: "",
        qualification: "",
        experienceYears: "",
        consultationFee: ""
    });

    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    const isDigitsOnly = (value) => /^[0-9]*$/.test(value);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "experienceYears" || name === "consultationFee") {
            if (!isDigitsOnly(value)) return;
        }

        if (name === "role" && value !== "2") {
            setForm(prev => ({
                ...prev,
                role: value,
                specialization: "",
                qualification: "",
                experienceYears: "",
                consultationFee: ""
            }));

            setFieldErrors(prev => ({
                ...prev,
                role: "",
                specialization: "",
                qualification: "",
                experienceYears: "",
                consultationFee: ""
            }));
            return;
        }

        setForm(prev => ({
            ...prev,
            [name]: value
        }));

        setFieldErrors(prev => ({
            ...prev,
            [name]: ""
        }));
    };

    const validate = () => {
        const errors = {};

        if (!form.firstName.trim()) errors.firstName = "First name is required";
        if (!form.lastName.trim()) errors.lastName = "Last name is required";
        if (!form.username.trim()) errors.username = "Username is required";
        if (!form.password.trim()) {
            errors.password = "Password is required";
        } else if (form.password.length < 4) {
            errors.password = "Password must be at least 4 characters";
        }

        if (!form.role.trim()) {
            errors.role = "Role is required";
        }

        if (form.role === "2") {
            if (!form.specialization.trim()) errors.specialization = "Specialization is required";
            if (!form.qualification.trim()) errors.qualification = "Qualification is required";

            if (!form.experienceYears.trim()) {
                errors.experienceYears = "Experience is required";
            } else if (!/^[0-9]+$/.test(form.experienceYears)) {
                errors.experienceYears = "Experience must be a number";
            }

            if (!form.consultationFee.trim()) {
                errors.consultationFee = "Consultation fee is required";
            } else if (!/^[0-9]+$/.test(form.consultationFee)) {
                errors.consultationFee = "Consultation fee must be a number";
            }
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setError("");
        setMessage("");

        if (!validate()) return;

        setLoading(true);

        try {
            const payload = {
                username: form.username.trim(),
                password: form.password,
                role: Number(form.role),
                clinicId,
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                doctorDetails:
                    Number(form.role) === 2
                        ? {
                              specialization: form.specialization.trim(),
                              qualification: form.qualification.trim(),
                              experienceYears: Number(form.experienceYears),
                              consultationFee: Number(form.consultationFee)
                          }
                        : null
            };

            const token = localStorage.getItem("token");

            const res = await fetch("/v1/clinic/users", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const text = await res.text();
                setError(text || "Failed to create user");
                return;
            }

            setMessage("User created successfully");

            setForm({
                firstName: "",
                lastName: "",
                username: "",
                password: "",
                role: "",
                specialization: "",
                qualification: "",
                experienceYears: "",
                consultationFee: ""
            });
            setFieldErrors({});
        } catch (err) {
            console.error(err);
            setError("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <h4 className="text-center mb-3">Register User</h4>

            {message && <div className="alert alert-success text-center">{message}</div>}
            {error && <div className="alert alert-danger text-center">{error}</div>}

            <form onSubmit={handleSubmit}>
                <div className="row mb-2">
                    <div className="col-md-6">
                        <input
                            className="form-control"
                            name="firstName"
                            placeholder="First Name"
                            value={form.firstName}
                            onChange={handleChange}
                        />
                        {fieldErrors.firstName && (
                            <small className="text-danger">{fieldErrors.firstName}</small>
                        )}
                    </div>

                    <div className="col-md-6">
                        <input
                            className="form-control"
                            name="lastName"
                            placeholder="Last Name"
                            value={form.lastName}
                            onChange={handleChange}
                        />
                        {fieldErrors.lastName && (
                            <small className="text-danger">{fieldErrors.lastName}</small>
                        )}
                    </div>
                </div>

                <div className="mb-2">
                    <input
                        className="form-control"
                        name="username"
                        placeholder="Username"
                        value={form.username}
                        onChange={handleChange}
                    />
                    {fieldErrors.username && (
                        <small className="text-danger">{fieldErrors.username}</small>
                    )}
                </div>

                <div className="mb-2">
                    <input
                        type="password"
                        className="form-control"
                        name="password"
                        placeholder="Password"
                        value={form.password}
                        onChange={handleChange}
                    />
                    {fieldErrors.password && (
                        <small className="text-danger">{fieldErrors.password}</small>
                    )}
                </div>

                <div className="mb-2">
                    <select
                        className="form-select"
                        name="role"
                        value={form.role}
                        onChange={handleChange}
                    >
                        <option value="">Select Role</option>
                        <option value="1">Admin</option>
                        <option value="2">Doctor</option>
                        <option value="3">Staff</option>
                    </select>
                    {fieldErrors.role && (
                        <small className="text-danger">{fieldErrors.role}</small>
                    )}
                </div>

                {form.role === "2" && (
                    <>
                        <div className="mb-2">
                            <input
                                className="form-control"
                                name="specialization"
                                placeholder="Specialization"
                                value={form.specialization}
                                onChange={handleChange}
                            />
                            {fieldErrors.specialization && (
                                <small className="text-danger">{fieldErrors.specialization}</small>
                            )}
                        </div>

                        <div className="mb-2">
                            <input
                                className="form-control"
                                name="qualification"
                                placeholder="Qualification"
                                value={form.qualification}
                                onChange={handleChange}
                            />
                            {fieldErrors.qualification && (
                                <small className="text-danger">{fieldErrors.qualification}</small>
                            )}
                        </div>

                        <div className="row mb-2">
                            <div className="col-md-6">
                                <input
                                    type="number"
                                    className="form-control"
                                    name="experienceYears"
                                    placeholder="Experience (Years)"
                                    value={form.experienceYears}
                                    onChange={handleChange}
                                />
                                {fieldErrors.experienceYears && (
                                    <small className="text-danger">{fieldErrors.experienceYears}</small>
                                )}
                            </div>

                            <div className="col-md-6">
                                <input
                                    type="number"
                                    className="form-control"
                                    name="consultationFee"
                                    placeholder="Consultation Fee"
                                    value={form.consultationFee}
                                    onChange={handleChange}
                                />
                                {fieldErrors.consultationFee && (
                                    <small className="text-danger">{fieldErrors.consultationFee}</small>
                                )}
                            </div>
                        </div>
                    </>
                )}

                <div className="d-flex gap-2 mt-3">
                    <button className="btn btn-primary w-100" disabled={loading}>
                        {loading ? "Creating..." : "Create User"}
                    </button>

                    <button type="button" className="btn btn-secondary w-100" onClick={onClose}>
                        Close
                    </button>
                </div>
            </form>
        </div>
    );
}

export default RegisterUser;
